"use client"

// ============================================
// CODE DETAILS SECTION COMPONENT
// ============================================
import {
  FaDatabase,
  FaEdit,
  FaPlus,
  FaTrash,
  FaChevronDown,
  FaChevronUp,
  FaSave,
  FaSpinner,
  FaMapMarkerAlt,
  FaTimesCircle,
} from "react-icons/fa"
import { INDIAN_STATES, BRAND_OPTIONS, CEMENT_SITE_TYPES } from "./shared"

const CodeDetailsSection = ({
  selectedUser,
  loading,
  userCodes,
  expandedCodes,
  editingCode,
  editedCodeData,
  addingSiteToCode,
  newSiteForm,
  newCodeForm,
  validation,
  depots,
  copiedAddresses,
  showAddressDropdown,
  showNewSiteAddressDropdown,
  // Handlers
  toggleCodeExpansion,
  startEditingCode,
  cancelEditingCode,
  handleEditedCodeChange,
  saveEditedCode,
  toggleAddingSite,
  handleNewSiteChange,
  addNewSiteToCode,
  handleNewCodeChange,
  handleNewCodeSiteChange,
  handleSubmitNewCode,
  deleteCode,
  setAddingSiteToCode,
  setShowAddressDropdown,
  setShowNewSiteAddressDropdown,
  applyCopiedAddress,
  applyCopiedAddressForNewSite,
  clearCopiedAddress,
  clearAllCopiedAddresses,
}) => {
  const renderCodeInfo = (code, isEditing) => {
    if (isEditing) {
      return (
        <div className="code-edit-form">
          <div className="edit-grid">
            <div className="form-group">
              <label>Code ID</label>
              <input
                type="text"
                value={editedCodeData.codeID || ""}
                onChange={(e) => handleEditedCodeChange("codeID", e.target.value.toUpperCase())}
                className="form-control"
                maxLength={20}
              />
            </div>
            <div className="form-group">
              <label>Balance</label>
              <input
                type="text"
                value={editedCodeData.balance || ""}
                onChange={(e) => handleEditedCodeChange("balance", e.target.value)}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Brand</label>
              <select
                value={editedCodeData.brand || ""}
                onChange={(e) => handleEditedCodeChange("brand", e.target.value)}
                className="form-control"
              >
                <option value="">--Select Brand--</option>
                {BRAND_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Cement Site Type</label>
              <select
                value={editedCodeData.cementSiteType || ""}
                onChange={(e) => handleEditedCodeChange("cementSiteType", e.target.value)}
                className="form-control"
              >
                <option value="">--Select Type--</option>
                {CEMENT_SITE_TYPES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Depot Name</label>
              <select
                value={editedCodeData.depoName || ""}
                onChange={(e) => handleEditedCodeChange("depoName", e.target.value)}
                className="form-control"
              >
                <option value="">--Select Depot--</option>
                {depots
                  .filter((depot) => depot.brandName === editedCodeData.brand)
                  .map((depot) => (
                    <option key={depot.id} value={depot.depoName}>
                      {depot.depoName}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <div className="form-section">
            <h5>Depot Account Details</h5>
            <div className="edit-grid">
              <div className="form-group">
                <label>Account Number</label>
                <input
                  type="text"
                  value={editedCodeData.depoAccount?.AccountNumber || ""}
                  onChange={(e) => handleEditedCodeChange("depoAccount.AccountNumber", e.target.value.toUpperCase())}
                  className="form-control"
                  maxLength={25}
                />
              </div>
              <div className="form-group">
                <label>Beneficiary Name</label>
                <input
                  type="text"
                  value={editedCodeData.depoAccount?.BeneficiaryName || ""}
                  onChange={(e) => handleEditedCodeChange("depoAccount.BeneficiaryName", e.target.value)}
                  className="form-control"
                  maxLength={50}
                />
              </div>
              <div className="form-group">
                <label>IFSC Code</label>
                <input
                  type="text"
                  value={editedCodeData.depoAccount?.IFSC || ""}
                  onChange={(e) => handleEditedCodeChange("depoAccount.IFSC", e.target.value.toUpperCase())}
                  className="form-control"
                  maxLength={11}
                />
              </div>
              <div className="form-group">
                <label>Bank & Branch</label>
                <input
                  type="text"
                  value={editedCodeData.depoAccount?.Branch || ""}
                  onChange={(e) => handleEditedCodeChange("depoAccount.Branch", e.target.value)}
                  className="form-control"
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button className="btn btn-secondary" onClick={cancelEditingCode}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={saveEditedCode}>
              <FaSave /> Save Changes
            </button>
          </div>
        </div>
      )
    }

    return (
      <>
        <div className="info-grid">
          <div className="info-row">
            <label>Code ID:</label>
            <span>{code.codeID || "N/A"}</span>
          </div>
          <div className="info-row">
            <label>Balance:</label>
            <span>{code.balance || "To be updated"}</span>
          </div>
          <div className="info-row">
            <label>Brand:</label>
            <span className="brand-badge">{code.brand || "N/A"}</span>
          </div>
          <div className="info-row">
            <label>Cement Site Type:</label>
            <span>{code.cementSiteType || "N/A"}</span>
          </div>
          <div className="info-row">
            <label>Depot Name:</label>
            <span>{code.depoName || "N/A"}</span>
          </div>
        </div>

        <div className="subsection">
          <h5>Depot Account Details</h5>
          {code.depoAccount ? (
            <div className="info-grid">
              <div className="info-row">
                <label>Account Number:</label>
                <span>{code.depoAccount.AccountNumber || "N/A"}</span>
              </div>
              <div className="info-row">
                <label>Beneficiary Name:</label>
                <span>{code.depoAccount.BeneficiaryName || "N/A"}</span>
              </div>
              <div className="info-row">
                <label>IFSC Code:</label>
                <span>{code.depoAccount.IFSC || "N/A"}</span>
              </div>
              <div className="info-row">
                <label>Bank & Branch:</label>
                <span>{code.depoAccount.Branch || "N/A"}</span>
              </div>
            </div>
          ) : (
            <p className="no-data">No depot account details available</p>
          )}
        </div>

        <div className="subsection">
          <h5>Site Addresses</h5>
          {code.sites && Object.keys(code.sites).length > 0 ? (
            <div className="sites-list">
              {Object.entries(code.sites).map(([siteKey, siteData]) => (
                <div key={siteKey} className="site-item">
                  <div className="site-badge">Site {siteKey}</div>
                  <div className="site-details">
                    <div className="info-row">
                      <label>Address:</label>
                      <span>{siteData.address || "N/A"}</span>
                    </div>
                    <div className="info-row">
                      <label>State:</label>
                      <span>{siteData.state || "N/A"}</span>
                    </div>
                    <div className="info-row">
                      <label>Pincode:</label>
                      <span>{siteData.pincode || "N/A"}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">No site addresses available</p>
          )}
        </div>
      </>
    )
  }

  const renderAddSiteForm = (code) => {
    if (addingSiteToCode !== code.id) return null

    return (
      <div className="add-site-form">
        <h5>
          <FaPlus /> Add New Site
        </h5>

        {copiedAddresses.length > 0 && (
          <div className="copied-addresses-section">
            <button
              type="button"
              className="btn btn-info"
              onClick={() => setShowNewSiteAddressDropdown(!showNewSiteAddressDropdown)}
            >
              <FaMapMarkerAlt /> Use Copied Address ({copiedAddresses.length})
            </button>

            {showNewSiteAddressDropdown && (
              <div className="addresses-dropdown">
                <div className="dropdown-header">
                  <span>Select from copied addresses:</span>
                  <button onClick={() => setShowNewSiteAddressDropdown(false)}>×</button>
                </div>
                <div className="addresses-list">
                  {copiedAddresses.map((addr, index) => (
                    <div key={index} className="address-item" onClick={() => applyCopiedAddressForNewSite(addr)}>
                      <div className="address-content">
                        <strong>{addr.address}</strong>
                        <span>
                          {addr.state}
                          {addr.pincode && `, ${addr.pincode}`}
                        </span>
                        <div className="address-meta">
                          {addr.companyName && <span className="tag company">{addr.companyName}</span>}
                          {addr.brand && <span className="tag brand">{addr.brand}</span>}
                        </div>
                      </div>
                      <button
                        className="remove-btn"
                        onClick={(e) => {
                          e.stopPropagation()
                          clearCopiedAddress(index)
                        }}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="site-form-fields">
          <div className="form-group">
            <label>Address</label>
            <input
              type="text"
              value={newSiteForm.address}
              onChange={(e) => handleNewSiteChange("address", e.target.value)}
              placeholder="Enter address"
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>State</label>
            <select
              value={newSiteForm.state}
              onChange={(e) => handleNewSiteChange("state", e.target.value)}
              className="form-control"
            >
              <option value="">--Select State--</option>
              {INDIAN_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Pincode (Optional)</label>
            <input
              type="text"
              value={newSiteForm.pincode}
              onChange={(e) => handleNewSiteChange("pincode", e.target.value)}
              placeholder="6-digit pincode"
              className="form-control"
              maxLength={6}
            />
          </div>
        </div>

        <div className="form-actions">
          <button className="btn btn-secondary" onClick={() => setAddingSiteToCode(null)}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => addNewSiteToCode(code.id)}
            disabled={!newSiteForm.address || !newSiteForm.state}
          >
            Add Site
          </button>
        </div>
      </div>
    )
  }

  const renderNewCodeForm = () => (
    <div className="new-code-form-section">
      <h3>
        <FaPlus /> Add New Code
      </h3>

      {copiedAddresses.length > 0 && (
        <div className="copied-addresses-info">
          <FaMapMarkerAlt />
          <span>Copied Site Addresses ({copiedAddresses.length})</span>
          <button onClick={clearAllCopiedAddresses} className="clear-all-btn">
            Clear All
          </button>
        </div>
      )}

      <div className="new-code-form">
        {/* Basic Info */}
        <div className="form-group">
          <label>
            Code ID <span className="required">*</span>
          </label>
          <input
            type="text"
            value={newCodeForm.codeID}
            onChange={(e) => handleNewCodeChange("codeID", e.target.value.toUpperCase())}
            placeholder="Max 20 chars, uppercase & digits"
            className={`form-control ${!validation.codeID?.isValid ? "has-error" : ""}`}
            maxLength={20}
          />
          {!validation.codeID?.isValid && <span className="error-text">{validation.codeID?.message}</span>}
        </div>

        <div className="form-group">
          <label>
            Brand <span className="required">*</span>
          </label>
          <select
            value={newCodeForm.brand}
            onChange={(e) => handleNewCodeChange("brand", e.target.value)}
            className={`form-control ${!validation.brand?.isValid ? "has-error" : ""}`}
          >
            <option value="">--Select Brand--</option>
            {BRAND_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {!validation.brand?.isValid && <span className="error-text">{validation.brand?.message}</span>}
        </div>

        <div className="form-group">
          <label>
            Cement Site Type <span className="required">*</span>
          </label>
          <select
            value={newCodeForm.cementSiteType}
            onChange={(e) => handleNewCodeChange("cementSiteType", e.target.value)}
            className="form-control"
          >
            <option value="">--Select Type--</option>
            {CEMENT_SITE_TYPES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {newCodeForm.brand && depots.filter((d) => d.brandName === newCodeForm.brand).length > 0 && (
          <div className="form-group">
            <label>
              Depot Name <span className="required">*</span>
            </label>
            <select
              value={newCodeForm.depoName}
              onChange={(e) => handleNewCodeChange("depoName", e.target.value)}
              className={`form-control ${!validation.depoName?.isValid ? "has-error" : ""}`}
            >
              <option value="">--Select Depot--</option>
              {depots
                .filter((depot) => depot.brandName === newCodeForm.brand)
                .map((depot) => (
                  <option key={depot.id} value={depot.depoName}>
                    {depot.depoName}
                  </option>
                ))}
            </select>
            {!validation.depoName?.isValid && <span className="error-text">{validation.depoName?.message}</span>}
          </div>
        )}

        <div className="form-group">
          <label>Balance</label>
          <input
            type="number"
            value={newCodeForm.balance || ""}
            onChange={(e) => handleNewCodeChange("balance", e.target.value)}
            placeholder="Enter balance amount"
            className="form-control"
            disabled
          />
        </div>

        {/* Depot Account Section */}
        <div className="form-section">
          <h4>Depot Account Details</h4>
          <div className="form-group">
            <label>
              Account Number <span className="required">*</span>
            </label>
            <input
              type="text"
              value={newCodeForm.depoAccount.AccountNumber}
              onChange={(e) => handleNewCodeChange("depoAccount.AccountNumber", e.target.value.toUpperCase())}
              placeholder="Max 25 chars"
              className="form-control"
              maxLength={25}
            />
          </div>
          <div className="form-group">
            <label>
              Beneficiary Name <span className="required">*</span>
            </label>
            <input
              type="text"
              value={newCodeForm.depoAccount.BeneficiaryName}
              onChange={(e) => handleNewCodeChange("depoAccount.BeneficiaryName", e.target.value)}
              placeholder="Letters only"
              className="form-control"
              maxLength={50}
            />
          </div>
          <div className="form-group">
            <label>
              IFSC Code <span className="required">*</span>
            </label>
            <input
              type="text"
              value={newCodeForm.depoAccount.IFSC}
              onChange={(e) => handleNewCodeChange("depoAccount.IFSC", e.target.value.toUpperCase())}
              placeholder="AAAA0XXXXXX"
              className="form-control"
              maxLength={11}
            />
          </div>
          <div className="form-group">
            <label>
              Bank & Branch <span className="required">*</span>
            </label>
            <input
              type="text"
              value={newCodeForm.depoAccount.Branch}
              onChange={(e) => handleNewCodeChange("depoAccount.Branch", e.target.value)}
              placeholder="Bank name and branch"
              className="form-control"
            />
          </div>
        </div>

        {/* Site Address Section */}
        <div className="form-section">
          <h4>Site Address</h4>

          {copiedAddresses.length > 0 && (
            <div className="copied-addresses-section">
              <button
                type="button"
                className="btn btn-info"
                onClick={() => setShowAddressDropdown(!showAddressDropdown)}
              >
                <FaMapMarkerAlt /> Use Copied Address ({copiedAddresses.length})
              </button>

              {showAddressDropdown && (
                <div className="addresses-dropdown">
                  <div className="dropdown-header">
                    <span>Select from copied addresses:</span>
                    <button onClick={() => setShowAddressDropdown(false)}>×</button>
                  </div>
                  <div className="addresses-list">
                    {copiedAddresses.map((addr, index) => (
                      <div key={index} className="address-item" onClick={() => applyCopiedAddress(addr)}>
                        <div className="address-content">
                          <strong>{addr.address}</strong>
                          <span>
                            {addr.state}
                            {addr.pincode && `, ${addr.pincode}`}
                          </span>
                          <div className="address-meta">
                            {addr.companyName && <span className="tag company">{addr.companyName}</span>}
                            {addr.brand && <span className="tag brand">{addr.brand}</span>}
                          </div>
                        </div>
                        <button
                          className="remove-btn"
                          onClick={(e) => {
                            e.stopPropagation()
                            clearCopiedAddress(index)
                          }}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label>
              Address <span className="required">*</span>
            </label>
            <input
              type="text"
              value={newCodeForm.sites[1].address}
              onChange={(e) => handleNewCodeSiteChange("address", e.target.value)}
              placeholder="Enter address"
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>
              State <span className="required">*</span>
            </label>
            <select
              value={newCodeForm.sites[1].state}
              onChange={(e) => handleNewCodeSiteChange("state", e.target.value)}
              className="form-control"
            >
              <option value="">--Select State--</option>
              {INDIAN_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Pincode (Optional)</label>
            <input
              type="text"
              value={newCodeForm.sites[1].pincode}
              onChange={(e) => handleNewCodeSiteChange("pincode", e.target.value)}
              placeholder="6-digit pincode"
              className="form-control"
              maxLength={6}
            />
          </div>
          {!validation.sites?.isValid && <span className="error-text">{validation.sites?.message}</span>}
        </div>

        <div className="form-actions">
          <button className="btn btn-primary btn-lg" onClick={handleSubmitNewCode} disabled={loading}>
            {loading ? (
              <>
                <FaSpinner className="spinner" /> Submitting...
              </>
            ) : (
              <>
                <FaSave /> Add New Code
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="code-details-tab">
      <div className="section-header">
        <FaDatabase className="section-icon" />
        <h2>Code Details</h2>
      </div>
      <p className="section-subtitle">Manage code details for {selectedUser.username || selectedUser.email}</p>

      <div className="code-details-container">
        <div className="code-cards-section">
          <h3>User Codes ({userCodes.length})</h3>

          {loading ? (
            <div className="loading-state">
              <FaSpinner className="spinner" /> Loading codes...
            </div>
          ) : userCodes.length === 0 ? (
            <div className="empty-state">
              <FaDatabase className="empty-icon" />
              <p>No codes found for this user.</p>
            </div>
          ) : (
            <div className="code-cards">
              {userCodes.map((code) => (
                <div key={code.id} className={`code-card ${expandedCodes[code.id] ? "expanded" : ""}`}>
                  <div className="code-header" onClick={() => toggleCodeExpansion(code.id)}>
                    <div className="code-title">
                      <span className="code-id">ID: {code.codeID}</span>
                      <span className="code-brand">Brand: {code.brand}</span>
                    </div>
                    <div className="code-actions">
                      <button
                        className="icon-btn edit"
                        onClick={(e) => {
                          e.stopPropagation()
                          startEditingCode(code)
                        }}
                        title="Edit Code"
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="icon-btn add"
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleAddingSite(code.id)
                        }}
                        title="Add New Site"
                      >
                        <FaPlus />
                      </button>
                      <button
                        className="icon-btn delete"
                        onClick={(e) => {
                          e.stopPropagation()
                          deleteCode(code.id)
                        }}
                        title="Delete Code"
                      >
                        <FaTimesCircle />
                      </button>
                      {expandedCodes[code.id] ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                  </div>

                  {expandedCodes[code.id] && (
                    <div className="code-details">
                      {renderCodeInfo(code, editingCode === code.id)}
                      {renderAddSiteForm(code)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {renderNewCodeForm()}
      </div>
    </div>
  )
}

export default CodeDetailsSection
